(() => {
  if (customElements.get('shirt-pack-556-upgrade')) return;

  customElements.define('shirt-pack-556-upgrade', class ShirtPack556Upgrade extends HTMLElement {
    constructor() {
      super();
      this.nativeFetch = window.__nativeFetch || window.fetch.bind(window);
      this.checkoutUrl = this.normalizeUrl(this.dataset.checkoutUrl, '/checkout');
      this.starterUrl = this.normalizeUrl(this.dataset.starterUrl, '/pages/556-shirt-pack');
      this.starterProductId = Number(this.dataset.starterProductId || 0);
      this.aovProductId = Number(this.dataset.aovProductId || 0);
      this.insuranceVariantId = Number(this.dataset.insuranceVariantId || 0);
      // Older saved section settings can still reference the retired variant.
      if (this.insuranceVariantId === 50757737873703) {
        const standardVariantId = Number(window.theme?.shopSettings?.shippingInsuranceVariantId);
        this.insuranceVariantId = standardVariantId && standardVariantId !== 50757737873703
          ? standardVariantId
          : 51706610188583;
      }
      this.insuranceVariantIds = new Set([
        this.insuranceVariantId,
        ...(Array.isArray(window.theme?.shopSettings?.shippingInsuranceVariantIds)
          ? window.theme.shopSettings.shippingInsuranceVariantIds
          : [51706610188583, 51706610221351, 51706610254119]),
        ...(Array.isArray(window.theme?.shopSettings?.shippingInsuranceLegacyVariantIds)
          ? window.theme.shopSettings.shippingInsuranceLegacyVariantIds
          : [50757737873703]),
      ].map(Number).filter(Boolean));
      this.insurancePrice = this.dataset.insurancePrice || '$9.95';
      this.insuranceAvailable = true;
      this.state = {
        tier: this.dataset.defaultTier || '300',
        size: this.dataset.defaultSize || 'Large',
        busy: false,
      };
    }

    connectedCallback() {
      this.shirtVariants = this.parseJson('[data-sp556u-shirt-variants]', {});
      this.packageButtons = Array.from(this.querySelectorAll('[data-sp556u-package]'));
      this.sizeInputs = Array.from(this.querySelectorAll('[data-sp556u-size]'));
      this.upgradeButton = this.querySelector('[data-sp556u-upgrade]');
      this.stickyBar = this.querySelector('[data-sp556u-sticky]');
      this.stickyUpgradeButton = this.querySelector('[data-sp556u-sticky-upgrade]');
      this.offerPanel = this.querySelector('[data-sp556u-panel="offer"]');
      this.declineButtons = Array.from(this.querySelectorAll('[data-sp556u-decline]'));
      this.galleryLinks = Array.from(this.querySelectorAll('[data-sp556u-gallery-link]'));
      this.packages = this.packageButtons.map((button) => this.readPackage(button));

      const configuredPackage = this.getPackage(this.state.tier);
      const firstAvailable = this.packages.find((item) => item.available && item.variantId && item.giftVariantId);
      if (!configuredPackage?.available && firstAvailable) this.state.tier = firstAvailable.tier;

      this.setText('[data-sp556u-protection-price]', this.insurancePrice);
      this.bindEvents();
      this.bindStickyCta();
      this.updateView();
      this.initializeFromCart();
    }

    bindEvents() {
      this.packageButtons.forEach((button) => {
        button.addEventListener('click', () => {
          if (this.state.busy || button.dataset.available !== 'true') return;
          this.state.tier = button.dataset.tier;
          this.showError('');
          this.updateView();
          this.track('sp556_upgrade_tier_selected', this.trackingPayload());
        });
        button.addEventListener('keydown', (event) => this.handlePackageKeydown(event, button));
      });

      this.sizeInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (!input.checked || this.state.busy) return;
          this.state.size = input.value;
          this.querySelector('[data-sp556u-size-step]')?.classList.remove('is-attention');
          this.showError('');
          this.updateView();
          this.track('sp556_upgrade_size_selected', this.trackingPayload());
        });
      });

      this.upgradeButton?.addEventListener('click', () => this.acceptUpgrade('primary'));
      this.stickyUpgradeButton?.addEventListener('click', () => this.acceptUpgrade('sticky_gallery'));
      this.galleryLinks.forEach((link) => {
        link.addEventListener('click', () => {
          this.track('sp556_upgrade_collection_viewed', this.trackingPayload({
            button_location: 'package_selector',
          }));
        });
      });
      this.declineButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.continueToCheckout(button);
        });
      });
    }

    handlePackageKeydown(event, currentButton) {
      const navigationKeys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'];
      if (!navigationKeys.includes(event.key) || this.state.busy) return;

      const availableButtons = this.packageButtons.filter((button) => (
        button.dataset.available === 'true' && !button.disabled
      ));
      if (!availableButtons.length) return;

      event.preventDefault();
      const currentIndex = Math.max(0, availableButtons.indexOf(currentButton));
      let nextIndex = currentIndex;

      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = availableButtons.length - 1;
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % availableButtons.length;
      }
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + availableButtons.length) % availableButtons.length;
      }

      const nextButton = availableButtons[nextIndex];
      nextButton.focus();
      nextButton.click();
    }

    bindStickyCta() {
      if (!this.stickyBar || !this.stickyUpgradeButton) return;
      this.boundStickyUpdate = () => this.requestStickyUpdate();
      window.addEventListener('scroll', this.boundStickyUpdate, { passive: true });
      window.addEventListener('resize', this.boundStickyUpdate, { passive: true });
      this.requestStickyUpdate();
    }

    requestStickyUpdate() {
      if (this.stickyFrame) return;
      this.stickyFrame = window.requestAnimationFrame(() => {
        this.stickyFrame = 0;
        this.updateStickyCta();
      });
    }

    updateStickyCta() {
      if (!this.stickyBar || !this.stickyUpgradeButton || !this.upgradeButton) return;

      const mainCtaRect = this.upgradeButton.getBoundingClientRect();
      const componentRect = this.getBoundingClientRect();
      const footer = document.querySelector('footer');
      const footerIsClear = !footer || footer.getBoundingClientRect().top > window.innerHeight - 12;
      const offerIsVisible = this.offerPanel && !this.offerPanel.hidden;
      const shouldShow = (
        window.innerWidth <= 749 &&
        offerIsVisible &&
        !this.stickyUpgradeButton.disabled &&
        mainCtaRect.bottom < -8 &&
        componentRect.bottom > 160 &&
        footerIsClear
      );

      this.stickyBar.classList.toggle('is-visible', shouldShow);
      this.stickyBar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      this.stickyUpgradeButton.tabIndex = shouldShow ? 0 : -1;
    }

    async initializeFromCart() {
      try {
        const [cart] = await Promise.all([
          this.fetchCart(),
          this.loadInsuranceDetails(),
        ]);

        if (this.hasCompleteCollection(cart)) {
          this.showPanel('completed');
          return;
        }

        const starterLines = this.getStarterLines(cart);
        if (!starterLines.length) {
          this.showPanel('fallback');
          return;
        }

        this.applyStarterSelections(starterLines[0]);
        this.showPanel('offer');
        this.updateView();
        this.track('sp556_upgrade_viewed', this.trackingPayload({
          starter_variant_id: starterLines[0].variant_id,
          starter_sku: starterLines[0].sku || undefined,
        }));
      } catch (error) {
        this.showPanel('fallback');
      }
    }

    applyStarterSelections(line) {
      const properties = line?.properties || {};
      const variantOptions = Array.isArray(line?.variant_options) ? line.variant_options : [];
      const design = properties._shirt_design || variantOptions[0] || '';
      const size = properties._shirt_size || variantOptions[1] || '';

      if (this.shirtVariants[size]) {
        this.state.size = size;
        this.sizeInputs.forEach((input) => { input.checked = input.value === size; });
      }

      const summary = [design, size].filter(Boolean).join(', ');
      if (summary) {
        this.setText(
          '[data-sp556u-starter-summary]',
          `${design} · ${size} shirt saved.`
        );
      }
    }

    readPackage(button) {
      return {
        tier: button.dataset.tier || '',
        label: button.dataset.label || '',
        rounds: button.dataset.rounds || '',
        bonus: button.dataset.bonus || '',
        includedValue: button.dataset.includedValue || '',
        variantId: Number(button.dataset.variantId || 0),
        variantSku: button.dataset.variantSku || '',
        variantTitle: button.dataset.variantTitle || '',
        giftVariantId: Number(button.dataset.giftVariantId || 0),
        giftVariantSku: button.dataset.giftVariantSku || '',
        giftProductId: Number(button.dataset.giftProductId || 0),
        price: button.dataset.price || '',
        priceCents: Number(button.dataset.priceCents || 0),
        available: button.dataset.available === 'true',
      };
    }

    updateView() {
      const selectedPackage = this.getSelectedPackage();
      const shirts = this.getSelectedShirts();
      const shirtsAvailable = shirts.length === 3 && shirts.every((item) => Number(item.id) && item.available !== false);
      const packageAvailable = Boolean(
        selectedPackage?.available &&
        selectedPackage.variantId &&
        selectedPackage.giftVariantId
      );
      const insuranceAvailable = !this.insuranceVariantId || this.insuranceAvailable;
      const canUpgrade = packageAvailable && shirtsAvailable && insuranceAvailable && Boolean(this.state.size) && !this.state.busy;

      this.packageButtons.forEach((button) => {
        const selected = button.dataset.tier === this.state.tier;
        const unavailable = button.dataset.available !== 'true';
        button.classList.toggle('is-selected', selected);
        button.classList.toggle('is-unavailable', unavailable);
        button.setAttribute('aria-checked', selected ? 'true' : 'false');
        button.disabled = this.state.busy || unavailable;
        button.tabIndex = selected && !unavailable ? 0 : -1;
      });

      this.sizeInputs.forEach((input) => {
        input.checked = input.value === this.state.size;
        input.disabled = this.state.busy;
      });

      if (selectedPackage) {
        this.setText(
          '[data-sp556u-cta]',
          this.state.busy ? 'Replacing Your Package…' : `Yes — Switch to the ${selectedPackage.price} Package`
        );
        this.setText(
          '[data-sp556u-sticky-selection]',
          `${selectedPackage.label} · ${selectedPackage.price} total`
        );
      }

      if (this.upgradeButton) {
        this.upgradeButton.disabled = !canUpgrade;
        this.upgradeButton.setAttribute('aria-busy', this.state.busy ? 'true' : 'false');
      }
      if (this.stickyUpgradeButton) {
        this.stickyUpgradeButton.disabled = !canUpgrade;
        this.stickyUpgradeButton.textContent = this.state.busy ? 'Updating…' : 'Upgrade';
        this.stickyUpgradeButton.setAttribute('aria-busy', this.state.busy ? 'true' : 'false');
      }

      this.requestStickyUpdate();

      if (!this.state.busy && selectedPackage && (!packageAvailable || !shirtsAvailable || !insuranceAvailable)) {
        this.showError(
          !packageAvailable
            ? 'This upgrade is temporarily unavailable. Choose another package or keep your 100-round range pack.'
            : !shirtsAvailable
              ? `The complete shirt set is not currently available in ${this.state.size}. Choose another size.`
              : 'Paid shipping protection is temporarily unavailable. Please try again shortly.'
        );
      }
    }

    async acceptUpgrade(buttonLocation = 'primary') {
      if (this.state.busy) return;

      const selectedPackage = this.getSelectedPackage();
      const shirts = this.getSelectedShirts();
      if (!selectedPackage?.available || !selectedPackage.variantId || !selectedPackage.giftVariantId) {
        this.showError('Choose an available upgrade package.');
        return;
      }
      if (!this.state.size || shirts.length !== 3 || shirts.some((item) => !Number(item.id) || item.available === false)) {
        this.querySelector('[data-sp556u-size-step]')?.classList.add('is-attention');
        this.querySelector('[data-sp556u-size-step]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.showError('Choose an available shirt size for the complete three-shirt set.');
        return;
      }

      this.setBusy(true);
      this.showError('');
      const upgradeSession = `sp556-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      let upgradeAdded = false;

      try {
        const cartBefore = await this.fetchCart();
        if (this.hasCompleteCollection(cartBefore)) {
          this.showPanel('completed');
          return;
        }

        const starterLines = this.getStarterLines(cartBefore);
        if (!starterLines.length) {
          this.showPanel('fallback');
          return;
        }

        const items = this.buildUpgradeItems(selectedPackage, shirts, upgradeSession);
        const insuranceItem = this.buildInsuranceItem(cartBefore, upgradeSession);
        if (insuranceItem) items.push(insuranceItem);

        await this.postJson('cart/add.js', { items });
        upgradeAdded = true;

        const cartWithUpgrade = await this.fetchCart();
        await this.removeStarterLines(cartWithUpgrade);
        await this.ensureInsuranceAttribute(cartBefore).catch(() => {});

        window.SubscriptionInsuranceCore?.clearCartCache?.();
        this.track('sp556_upgrade_accepted', this.trackingPayload({
          button_location: buttonLocation,
          upgrade_session: upgradeSession,
          paid_variant_id: selectedPackage.variantId,
          paid_sku: selectedPackage.variantSku || undefined,
          gift_variant_id: selectedPackage.giftVariantId,
          gift_sku: selectedPackage.giftVariantSku || undefined,
          shirt_variant_ids: shirts.map((item) => item.id).join(','),
          package_price_cents: selectedPackage.priceCents,
        }));
        window.location.href = this.checkoutUrl;
      } catch (error) {
        if (upgradeAdded) await this.rollbackUpgrade(upgradeSession).catch(() => {});
        this.setBusy(false);
        this.showError(error.message || 'We could not replace the starter package. Your original range pack is still in the cart. Please try again.');
      }
    }

    async continueToCheckout(button) {
      if (this.state.busy) return;

      this.setBusy(true);
      this.showError('');
      this.track('sp556_upgrade_declined', this.trackingPayload({
        action: 'keep_100_round_pack',
        button_location: button?.classList?.contains('sp556u__status-checkout') ? 'top_status' : 'below_upgrade_cta',
      }));

      try {
        if (this.insuranceVariantId && !this.insuranceAvailable) {
          throw new Error('Paid shipping protection is temporarily unavailable. Please try again shortly.');
        }

        const cart = await this.fetchCart();
        await this.ensureInsuranceInCart(cart);
        window.SubscriptionInsuranceCore?.clearCartCache?.();
        window.location.href = this.checkoutUrl;
      } catch (error) {
        this.setBusy(false);
        this.showError(error.message || 'Paid shipping protection could not be added. Please try again before checkout.');
      }
    }

    buildUpgradeItems(selectedPackage, shirts, upgradeSession) {
      const shared = {
        _upgrade_from: '556-shirt-pack',
        _upgrade_session: upgradeSession,
      };

      const items = [{
        id: selectedPackage.variantId,
        quantity: 1,
        properties: {
          ...shared,
          _aov_offer: '556-complete-collection',
          _aov_selected_package: selectedPackage.variantTitle || selectedPackage.label,
          _aov_gift_tier: selectedPackage.tier,
          _aov_shirt_size: this.state.size,
        },
      }, {
        id: selectedPackage.giftVariantId,
        quantity: 1,
        properties: {
          ...shared,
          _free_gift: 'true',
          _aov_offer: '556-complete-collection-gift',
          _aov_gift_type: 'complete-collection',
          _aov_gift_tier: selectedPackage.tier,
          _aov_parent_variant_id: String(selectedPackage.variantId),
          _aov_parent_product_id: String(this.aovProductId || ''),
        },
      }];

      shirts.forEach((shirt) => {
        items.push({
          id: Number(shirt.id),
          quantity: 1,
          properties: {
            ...shared,
            _free_gift: 'true',
            _aov_offer: '556-complete-collection',
            _aov_parent_offer: '556-complete-collection-gift',
            _aov_gift_type: 'patriot-shirt-pack',
            _aov_parent_variant_id: String(selectedPackage.variantId),
            _aov_parent_product_id: String(this.aovProductId || ''),
            _aov_gift_tier: selectedPackage.tier,
            _shirt_pack_size: this.state.size,
            _shirt_pack_design: shirt.design || shirt.title || 'Patriot Tee',
          },
        });
      });

      return items;
    }

    buildInsuranceItem(cart, upgradeSession) {
      if (!this.insuranceVariantId) return null;
      const alreadyPresent = Array.isArray(cart?.items) && cart.items.some((item) => (
        this.insuranceVariantIds.has(Number(item.variant_id))
      ));
      if (alreadyPresent) return null;

      return {
        id: this.insuranceVariantId,
        quantity: 1,
        properties: {
          _upgrade_from: '556-shirt-pack',
          _upgrade_session: upgradeSession,
        },
      };
    }

    async removeStarterLines(cart) {
      const updates = {};
      this.getStarterLines(cart).forEach((item) => {
        if (item.key) updates[item.key] = 0;
      });

      if (!Object.keys(updates).length) {
        throw new Error('We could not find the original range pack to replace. The upgrade was not kept.');
      }

      await this.postJson('cart/update.js', { updates });
    }

    async rollbackUpgrade(upgradeSession) {
      const cart = await this.fetchCart();
      const updates = {};

      (cart.items || []).forEach((item) => {
        if (item?.properties?._upgrade_session === upgradeSession && item.key) updates[item.key] = 0;
      });

      if (Object.keys(updates).length) await this.postJson('cart/update.js', { updates });
    }

    getStarterLines(cart) {
      return Array.isArray(cart?.items) ? cart.items.filter((item) => {
        const properties = item?.properties || {};
        return properties._offer === '556-tripwire-shirt' || (
          this.starterProductId && Number(item.product_id) === this.starterProductId
        );
      }) : [];
    }

    hasCompleteCollection(cart) {
      const packageIds = new Set(this.packages.map((item) => item.variantId).filter(Boolean));
      return Array.isArray(cart?.items) && cart.items.some((item) => {
        const properties = item?.properties || {};
        return packageIds.has(Number(item.variant_id)) || (
          properties._aov_offer === '556-complete-collection' &&
          !properties._free_gift &&
          Number(item.product_id) === this.aovProductId
        );
      });
    }

    getSelectedPackage() {
      return this.getPackage(this.state.tier);
    }

    getPackage(tier) {
      return this.packages.find((item) => item.tier === String(tier || '')) || null;
    }

    getSelectedShirts() {
      const shirts = this.shirtVariants?.[this.state.size];
      return Array.isArray(shirts) ? shirts : [];
    }

    async fetchCart() {
      const response = await this.nativeFetch(`${this.getRoot()}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Unable to read your cart. Please try again.');
      return response.json();
    }

    async loadInsuranceDetails() {
      if (!this.insuranceVariantId) return null;

      try {
        const response = await this.nativeFetch(`${this.getRoot()}variants/${this.insuranceVariantId}.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return null;

        const variant = await response.json();
        if (Number.isFinite(Number(variant?.price))) {
          this.insurancePrice = this.formatMoney(Number(variant.price));
          this.setText('[data-sp556u-protection-price]', this.insurancePrice);
        }
        this.insuranceAvailable = variant?.available !== false;
        return variant;
      } catch (error) {
        return null;
      }
    }

    async postJson(path, body) {
      const response = await this.nativeFetch(`${this.getRoot()}${path}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.description || payload.message || 'Unable to update your cart.');
      }
      return payload;
    }

    async ensureInsuranceAttribute(cart) {
      if (!this.insuranceVariantId) return;
      const attributes = { ...(cart?.attributes || {}) };
      if (attributes['shipping-insurance'] === 'true') return;
      attributes['shipping-insurance'] = 'true';
      await this.postJson('cart/update.js', { attributes });
    }

    async ensureInsuranceInCart(cart) {
      if (!this.insuranceVariantId) return;

      const items = Array.isArray(cart?.items) ? cart.items : [];
      const hasInsurance = items.some((item) => this.insuranceVariantIds.has(Number(item.variant_id)));
      const hasOtherItems = items.some((item) => !this.insuranceVariantIds.has(Number(item.variant_id)));
      if (!hasOtherItems) return;

      if (hasInsurance) {
        await this.ensureInsuranceAttribute(cart);
        return;
      }

      await this.postJson('cart/update.js', {
        updates: { [this.insuranceVariantId]: 1 },
        attributes: {
          ...(cart?.attributes || {}),
          'shipping-insurance': 'true',
        },
      });
    }

    showPanel(name) {
      this.querySelectorAll('[data-sp556u-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.sp556uPanel !== name;
      });
      this.requestStickyUpdate();
    }

    setBusy(busy) {
      this.state.busy = busy;
      this.updateView();
    }

    showError(message) {
      const error = this.querySelector('[data-sp556u-error]');
      if (!error) return;
      error.textContent = message || '';
      error.hidden = !message;
    }

    parseJson(selector, fallback) {
      const source = this.querySelector(selector);
      if (!source?.textContent) return fallback;
      try {
        return JSON.parse(source.textContent);
      } catch (error) {
        return fallback;
      }
    }

    setText(selector, value) {
      this.querySelectorAll(selector).forEach((element) => { element.textContent = value || ''; });
    }

    normalizeUrl(value, fallback) {
      const url = String(value || fallback || '').trim();
      return url || fallback;
    }

    formatMoney(cents) {
      const value = Number(cents || 0) / 100;
      const currency = window.Shopify?.currency?.active || 'USD';
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value);
      } catch (error) {
        return `$${value.toFixed(2)}`;
      }
    }

    getRoot() {
      return window.Shopify?.routes?.root || '/';
    }

    trackingPayload(extra = {}) {
      const selectedPackage = this.getSelectedPackage();
      return this.cleanPayload({
        event_category: '556_shirt_pack_upgrade',
        offer_id: '556-shirt-pack-upgrade',
        selected_tier: selectedPackage?.tier,
        selected_label: selectedPackage?.label,
        selected_size: this.state.size,
        paid_variant_id: selectedPackage?.variantId,
        paid_sku: selectedPackage?.variantSku,
        gift_variant_id: selectedPackage?.giftVariantId,
        gift_sku: selectedPackage?.giftVariantSku,
        package_price: selectedPackage?.price,
        insurance_variant_id: this.insuranceVariantId || undefined,
        insurance_price: this.insurancePrice || undefined,
        page_location: window.location.href,
        page_path: window.location.pathname,
        ...extra,
      });
    }

    track(eventName, payload) {
      const clean = this.cleanPayload(payload || {});
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: eventName, ...clean });
      } catch (error) {}
      try {
        if (typeof window.gtag === 'function') window.gtag('event', eventName, clean);
      } catch (error) {}
      try {
        window.Shopify?.analytics?.publish?.(eventName, clean);
      } catch (error) {}
    }

    cleanPayload(payload) {
      return Object.keys(payload).reduce((clean, key) => {
        if (payload[key] !== undefined && payload[key] !== '') clean[key] = payload[key];
        return clean;
      }, {});
    }
  });
})();
